/**
 * RoofPolygonLayer Component
 * Renders roof polygons with STABLE keys to prevent flickering on zoom/pan.
 *
 * Key optimization: Uses polygon ID as the only key, avoiding remounting.
 * Previous implementation used coordinate-based keys that changed on edits,
 * causing React to unmount/remount the component and flicker.
 *
 * Features:
 * - Stable rendering without flickering on zoom/pan
 * - Support for vertex edit mode (EditablePolygon)
 * - Support for smooth edit mode (DraggableObstruction)
 * - Regular display mode with click/double-click handlers
 * - Memoized coordinate transformations
 */

import React, { useMemo, useCallback } from 'react';
import { Polygon } from 'react-leaflet';
import L from 'leaflet';

import { EditablePolygon } from './EditablePolygon';
import { DraggableObstruction } from '../obstructions/DraggableObstruction';

// Re-export Polygon interface for external use
export interface RoofPolygon {
  id: number;
  geometry: {
    type: string;
    coordinates: number[][][];
  };
  area_m2: number;
}

export interface RoofPolygonStyle {
  color: string;
  fillColor: string;
  fillOpacity: number;
  opacity: number;
  weight: number;
}

export interface RoofPolygonLayerProps {
  /** Array of roof polygons to render */
  polygons: RoofPolygon[];

  /** Currently active/selected polygon ID (primary selection) */
  selectedPolygon: number | null;

  /** Array of all selected polygon IDs (multi-selection) */
  selectedPolygons: number[];

  /** Current edit mode - 'site' or 'system' */
  editMode: 'site' | 'system';

  /** Whether to show all polygons or only selected */
  showAllPolygons: boolean;

  /** Map of polygon ID to edited coordinates */
  editedPolygonCoords: Record<number, [number, number][]>;

  /** Map of polygon ID to original coordinates (used when loading projects) */
  originalPolygonCoords?: Record<number, [number, number][]>;

  /** ID of polygon currently in vertex edit mode (double-click) */
  editingPolygonVertex: number | null;

  /** ID of polygon currently in smooth edit mode (single click) */
  selectedPolygonForEdit: number | null;

  /** Whether obstruction drawing is active */
  isDrawingObstruction: boolean;

  /** Whether segment drawing is active */
  isDrawingSegment: boolean;

  /** Whether roof selection mode is active */
  isRoofSelectMode: boolean;

  /** Whether add roof mode is active */
  isAddRoofMode: boolean;

  /** Whether to bypass polygon fetch (use saved geometry) */
  bypassPolygonFetch?: boolean;

  /** Function to get polygon style based on selection state */
  getPolygonStyle: (polygon: RoofPolygon) => RoofPolygonStyle;

  /** Handler for polygon click events */
  onPolygonClick: (id: number, shiftKey: boolean) => void;

  /** Handler for polygon double-click events (enters vertex edit mode) */
  onPolygonDoubleClick: (id: number) => void;

  /** Handler for polygon coordinate changes (from edit modes) */
  onPolygonMove: (id: number, coords: [number, number][]) => void;

  /** When true, click on a vertex removes it (no Alt key needed). Shown as "Eliminar nodo" in UI. */
  removeVertexOnClick?: boolean;

  /** When true, click on a polyline edge adds a vertex (no Alt key needed). */
  addVertexOnClick?: boolean;
}

/**
 * Extracts [lat, lng] coordinates from GeoJSON geometry.
 * GeoJSON uses [lng, lat] order, so we need to swap them for Leaflet.
 */
function extractCoordsFromGeometry(
  geometry: RoofPolygon['geometry']
): [number, number][] {
  if (!geometry?.coordinates?.[0]) {
    return [];
  }
  // GeoJSON: [lng, lat] -> Leaflet: [lat, lng]
  return geometry.coordinates[0].map(
    ([lng, lat]: number[]) => [lat, lng] as [number, number]
  );
}

/**
 * RoofPolygonLayer - Stable polygon rendering layer
 *
 * Performance optimizations:
 * - Uses stable keys (polygon.id only) to prevent remounting on zoom/pan
 * - Memoizes coordinate calculations
 * - Single-pass visibility filtering
 * - Callbacks are memoized at parent level via props
 */
export function RoofPolygonLayer({
  polygons,
  selectedPolygon,
  selectedPolygons,
  editMode,
  showAllPolygons,
  editedPolygonCoords,
  originalPolygonCoords = {},
  editingPolygonVertex,
  selectedPolygonForEdit,
  isDrawingObstruction,
  isDrawingSegment,
  isRoofSelectMode,
  isAddRoofMode,
  bypassPolygonFetch = false,
  getPolygonStyle,
  onPolygonClick,
  onPolygonDoubleClick,
  onPolygonMove,
  removeVertexOnClick = false,
  addVertexOnClick = false,
}: RoofPolygonLayerProps) {
  /**
   * Memoized function to get coordinates for a polygon.
   * Priority: edited > original > geometry extraction
   * Includes validation to prevent NaN coordinates.
   */
  const getPolygonCoords = useCallback(
    (polygon: RoofPolygon): [number, number][] => {
      let coords: [number, number][] = [];

      // Priority 1: Return edited coords if they exist
      if (editedPolygonCoords[polygon.id]?.length > 0) {
        coords = editedPolygonCoords[polygon.id];
      }
      // Priority 2: Return original coords (used when loading projects)
      else if (originalPolygonCoords[polygon.id]?.length > 0) {
        coords = originalPolygonCoords[polygon.id];
      }
      // Priority 3: Extract from geometry
      else {
        coords = extractCoordsFromGeometry(polygon.geometry);
      }

      // Validate coordinates - filter out any NaN values
      const validCoords = coords.filter(
        ([lat, lng]) => !isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng)
      );

      return validCoords;
    },
    [editedPolygonCoords, originalPolygonCoords]
  );

  /**
   * Memoized list of visible polygons based on current mode and selection.
   * Single-pass filter for performance.
   */
  const visiblePolygons = useMemo(() => {
    // In bypass mode, only render the polygon when actively editing its vertices
    if (bypassPolygonFetch) {
      if (editingPolygonVertex !== null) {
        return polygons.filter((p) => p.id === editingPolygonVertex);
      }
      return [];
    }

    // In system mode, hide all roof polygons
    if (editMode === 'system') {
      return [];
    }

    // Filter based on showAllPolygons toggle
    if (!showAllPolygons) {
      return polygons.filter((p) => p.id === selectedPolygon);
    }

    return polygons;
  }, [polygons, bypassPolygonFetch, editMode, showAllPolygons, selectedPolygon, editingPolygonVertex]);

  /**
   * Render a single polygon based on its current state.
   * Uses stable key based only on polygon ID.
   * Skips rendering if coordinates are invalid.
   */
  const renderPolygon = useCallback(
    (polygon: RoofPolygon) => {
      const coords = getPolygonCoords(polygon);

      // Skip rendering if no valid coordinates (prevents NaN errors)
      if (coords.length < 3) {
        console.warn(`[RoofPolygonLayer] Polygon ${polygon.id} has insufficient coordinates (${coords.length}), skipping render`);
        return null;
      }

      const isInVertexEdit = editingPolygonVertex === polygon.id;
      const isInSmoothEdit = selectedPolygonForEdit === polygon.id;

      // Vertex edit mode (double-click) - show EditablePolygon
      if (isInVertexEdit) {
        return (
          <EditablePolygon
            key={polygon.id}
            coordinates={coords}
            onCoordinatesChange={(newCoords) =>
              onPolygonMove(polygon.id, newCoords)
            }
            style={{
              color: '#10b981',
              fillColor: '#34d399',
              fillOpacity: 0.5,
              weight: 3,
            }}
            removeVertexOnClick={removeVertexOnClick}
            addVertexOnClick={addVertexOnClick}
          />
        );
      }

      // Smooth edit mode (single click) - show DraggableObstruction for drag/rotate/resize
      if (isInSmoothEdit) {
        return (
          <DraggableObstruction
            key={polygon.id}
            obstruction={coords}
            isSelected={true}
            onSelect={() => {}}
            onMove={(newCoords) => onPolygonMove(polygon.id, newCoords)}
            onRotate={(newCoords) => onPolygonMove(polygon.id, newCoords)}
            onDoubleClick={() => onPolygonDoubleClick(polygon.id)}
            onDuplicate={undefined}
          />
        );
      }

      // Regular display mode - use Polygon with stable key
      const style = getPolygonStyle(polygon);
      const isInteractive = editMode === 'site';

      // Build event handlers - only add click handler if interactive
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const eventHandlers = isInteractive
        ? {
            click: (e: any) => {
              // Multi-roof UX: Different click behavior based on mode
              const isInRoofList = selectedPolygons.includes(polygon.id);

              // Add Roof Mode: Allow clicking any polygon to add to roof list
              // Normal Mode: Only allow clicking roofs already in the list (to switch active)
              const canClick =
                isRoofSelectMode || isAddRoofMode || isInRoofList;

              if (canClick && !isDrawingObstruction && !isDrawingSegment) {
                L.DomEvent.stopPropagation(e);
                const shiftKey = e.originalEvent?.shiftKey || false;
                onPolygonClick(polygon.id, shiftKey);
              }
            },
          }
        : {};

      return (
        <Polygon
          key={polygon.id}
          positions={coords}
          pathOptions={{ ...style, pane: 'overlayPane' }}
          eventHandlers={eventHandlers}
        />
      );
    },
    [
      getPolygonCoords,
      editingPolygonVertex,
      selectedPolygonForEdit,
      getPolygonStyle,
      editMode,
      selectedPolygons,
      isRoofSelectMode,
      isAddRoofMode,
      isDrawingObstruction,
      isDrawingSegment,
      onPolygonClick,
      onPolygonDoubleClick,
      onPolygonMove,
      removeVertexOnClick,
      addVertexOnClick,
    ]
  );

  return <>{visiblePolygons.map(renderPolygon)}</>;
}

export default RoofPolygonLayer;
